/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Attributes from "./Attributes";
import AttributeValues from "./AttributeValues";
import SkillSet, { Skill } from "./SkillSet";

interface LegacySkill {
  name: string;
  stat: string;
  note?: string;
}

class SkillStats {
  static EXPERT = ' (expert)';

  static getSkillsMap(): Map<string, keyof AttributeValues> {
    return new Map<string, keyof AttributeValues>([
      ["Acrobatics",      "dexterity"],
      ["Animal Handling", "wisdom"],
      ["Arcana",          "intelligence"],
      ["Athletics",       "strength"],
      ["Culture",         "intelligence"],
      ["Deception",       "charisma"],
      ["Engineering",     "intelligence"],
      ["History",         "intelligence"],
      ["Initiative",      "dexterity"],
      ["Insight",         "wisdom"],
      ["Intimidation",    "charisma"],
      ["Investigation",   "intelligence"],
      ["Medicine",        "wisdom"],
      ["Nature",          "intelligence"],
      ["Perception",      "wisdom"],
      ["Performance",     "charisma"],
      ["Persuasion",      "charisma"],
      ["Religion",        "intelligence"],
      ["Sleight of Hand", "dexterity"],
      ["Stealth",         "dexterity"],
      ["Survival",        "wisdom"],
    ]);
  }

  static artisanTools: string[] = [
    "Alchemist's supplies",
    "Brewer's supplies",
    "Calligrapher's tools",
    "Carpenter's tools",
    "Cartographer's tools",
    "Cobbler's tools",
    "Cook's utensils",
    "Glassblower's tools",
    "Jeweler's tools",
    "Leatherworker's tools",
    "Mason's tools",
    "Painter's supplies",
    "Potter's tools",
    "Smith's tools",
    "Tinker's tools",
    "Weaver's tools",
    "Woodcarver's tools",
  ];
  static tools: string[] = [
    ... this.artisanTools,
    "Disguise Kit",
    "Forgery Kit",
    "Herbalist kit",
    "Navigator's tools",
    "Poisoner's kit",
    "Thieves' tools",
    "Vehicles (land)",
    "Vehicles (water)"
  ].sort();

  static packs: string[] = [
    "Burglar's Pack",
    "Diplomat's Pack",
    "Dungeoneer's Pack",
    "Entertainer's Pack",
    "Explorer's Pack",
    "Priest's Pack",
    "Scholar's Pack"
  ]

  static gamingSets: string[] = [
    "Dice Set",
    "Dragonchess Set",
    "Playing Card Set",
    "Three-Dragon Ante Set"
  ];

  static musicalInstruments: string[] = [
    "Bagpipes",
    "Drum",
    "Dulcimer",
    "Flute",
    "Lute",
    "Lyre",
    "Horn",
    "Pan Flute",
    "Shawm",
    "Viol"
  ];

   static importOpen5eSkills(legacySkills: any, attributes: AttributeValues, proficiency: number): Map<string, number> {
    const skillsObj = new Map();
    return skillsObj;
  }

  static importLegacySkills(legacySkills: LegacySkill[], attributes: AttributeValues, proficiency: number): SkillSet {
    const skillsSet = new SkillSet();
    for (const skill of legacySkills) {
      const attr = Attributes.getAttrName(skill.stat);
      if (attr != '') {
        let name = skill.name;
        for (const skillName of SkillStats.getSkillsMap().keys()) {
          if (name.toLowerCase() === skillName.toLowerCase()) {
            name = skillName;
            break;
          }
        }

        const attribute = SkillStats.getSkillsMap().get(name);
        if (attribute) {
          const isExpert = (skill.note === SkillStats.EXPERT) || (skill.note === ' (ex)');
          const newSkill = new Skill();
          newSkill.attribute = attribute;
          newSkill.level = isExpert ? 'expert' : 'proficient';
          skillsSet.skills[name] = newSkill;
        }
      }
    }
    return SkillSet.updateLevels(skillsSet, attributes, proficiency);
  }
}

export default SkillStats;
